import React from "react";
import PropTypes from "prop-types";
import { compose, withState, withHandlers } from "recompose";
import { Grid, MenuItem, Fab, Link, Typography } from "@material-ui/core";
import AlertBox from "../../../../../components/AlertBox/AlertBox";
import SelectWithTitle from "../../../../../components/SelectWithTitle/SelectWithTitle";
import SelectItem from "../../../../../components/SelectItem/SelectItem";
import InputWithTitle from "../../../../../components/InputWithTitle/InputWithTitle";
import HashIcon from "../../../../../components/HashIcon/HashIcon";
import CustomExpansionPanel from "../../../../../components/CustomExpansionPanel/CustomExpansionPanel";
import withErrorTooltip from "../../../../../hocs/withErrorTooltip";
import { ReactComponent as SearchIcon } from "../../../../../assets/icons/search-white.svg";
import {
  ACCOUNT_NUMBER,
  BUSINESS_ID,
  DPD,
  DPD_LOCAL,
  ACCOUNT_DETAILS,
  API_PORTAL,
  WEBHOOKS_API_PORTAL_INFO,
  WEBHOOKS_API_PORTAL_URL,
} from "../../../../../constants/strings";
import { INVALID_ACCOUNT_NUMBER } from "../../../../../features/customer/customer.constants";
import { getCustomersStartsWith } from "../../../../../features/customer/customer.service";
import { validateAccountNumber } from "../../../../../features/webhooks/webhook.validate";
import {
  DPD_BUSINESS_ID,
  DPD_LOCAL_BUSINESS_ID,
} from "../../../../../features/userDetailsForm/accountDetails/accountDetails.constants";
import DpdIcon from "../../../../../assets/icons/dpd.svg";
import DpdIconLocal from "../../../../../assets/icons/dpd-local.svg";
import styles from "./AccountDetails.module.scss";

const DecoratedInputWithError = withErrorTooltip(InputWithTitle);

const getCustomersList = (customersResponse) =>
  Array.isArray(customersResponse)
    ? customersResponse
    : customersResponse?.data || [];

const hasMatchingCustomer = (customersResponse, accountNumber, businessId) =>
  getCustomersList(customersResponse).some(
    (customer) =>
      String(customer?.account) === String(accountNumber) &&
      customer?.business === businessId
  );

const AccountDetails = ({
  businessId,
  accountNumber,
  isDpdAdmin,
  searchAccountNumber,
  setSearchAccountNumber,
  searchError,
  setSearchError,
  selectedBusinessId,
  setSelectedBusinessId,
  handleSearch,
}) => {
  const apiPortalNotice = (
    <Grid item xs={12}>
      <AlertBox
        variant="info"
        subtitle={
          <Typography variant="body2" style={{ color: "black" }}>
            {WEBHOOKS_API_PORTAL_INFO.split(API_PORTAL)[0]}
            <Link
              href={WEBHOOKS_API_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "black", textDecoration: "underline" }}
            >
              {API_PORTAL}
            </Link>
            {WEBHOOKS_API_PORTAL_INFO.split(API_PORTAL)[1]}
          </Typography>
        }
      />
    </Grid>
  );

  if (isDpdAdmin) {
    return (
      <CustomExpansionPanel title={ACCOUNT_DETAILS} defaultExpanded>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <SelectWithTitle
              label={BUSINESS_ID}
              value={selectedBusinessId}
              onChange={(e) => {
                setSelectedBusinessId(e.target.value);
                setSearchError("");
              }}
            >
              <MenuItem value={DPD_BUSINESS_ID}>
                <SelectItem icon={DpdIcon} title={DPD} />
              </MenuItem>
              <MenuItem value={DPD_LOCAL_BUSINESS_ID}>
                <SelectItem icon={DpdIconLocal} title={DPD_LOCAL} />
              </MenuItem>
            </SelectWithTitle>
          </Grid>
          <Grid item xs={12} md={5}>
            <div className={styles.searchContainer}>
              <DecoratedInputWithError
                label={ACCOUNT_NUMBER}
                icon={<HashIcon />}
                input={{
                  value: searchAccountNumber,
                  onChange: (e) => {
                    setSearchAccountNumber(e.target.value);
                    setSearchError("");
                  },
                  onBlur: () => {},
                }}
                meta={{
                  valid: !searchError,
                  touched: !!searchError,
                  active: false,
                  error: searchError,
                }}
                inputProps={{ maxLength: 7 }}
                onKeyDown={(e) => e.keyCode === 13 && handleSearch()}
                showErrorsOnRender
              />
              <Fab
                size="small"
                onClick={handleSearch}
                disabled={
                  !searchAccountNumber.trim() ||
                  !validateAccountNumber(searchAccountNumber)
                }
                className={styles.searchButton}
              >
                <SearchIcon className={styles.searchIcon} />
              </Fab>
            </div>
          </Grid>
          {apiPortalNotice}
        </Grid>
      </CustomExpansionPanel>
    );
  }

  return (
    <CustomExpansionPanel title={ACCOUNT_DETAILS} defaultExpanded>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <SelectWithTitle
            label={BUSINESS_ID}
            value={businessId || DPD_BUSINESS_ID}
            disabled
          >
            <MenuItem value={DPD_BUSINESS_ID}>
              <SelectItem icon={DpdIcon} title={DPD} />
            </MenuItem>
            <MenuItem value={DPD_LOCAL_BUSINESS_ID}>
              <SelectItem icon={DpdIconLocal} title={DPD_LOCAL} />
            </MenuItem>
          </SelectWithTitle>
        </Grid>
        <Grid item xs={12} md={6}>
          <InputWithTitle
            label={ACCOUNT_NUMBER}
            icon={<HashIcon />}
            input={{
              value: accountNumber,
              onChange: () => {},
              onBlur: () => {},
            }}
            disabled
            required={!isDpdAdmin}
            inputProps={{ maxLength: 7 }}
          />
        </Grid>
        {apiPortalNotice}
      </Grid>
    </CustomExpansionPanel>
  );
};

AccountDetails.propTypes = {
  businessId: PropTypes.number,
  accountNumber: PropTypes.string,
  isDpdAdmin: PropTypes.bool,
  searchAccountNumber: PropTypes.string,
  setSearchAccountNumber: PropTypes.func,
  searchError: PropTypes.string,
  setSearchError: PropTypes.func,
  selectedBusinessId: PropTypes.number,
  setSelectedBusinessId: PropTypes.func,
  handleSearch: PropTypes.func,
};

export default compose(
  withState("searchAccountNumber", "setSearchAccountNumber", ""),
  withState("searchError", "setSearchError", ""),
  withState("selectedBusinessId", "setSelectedBusinessId", DPD_BUSINESS_ID),
  withHandlers({
    handleSearch:
      ({
        searchAccountNumber,
        selectedBusinessId,
        onAccountSearch,
        setSearchError,
      }) =>
      async () => {
        const trimmedAccountNumber = searchAccountNumber.trim();

        if (
          !trimmedAccountNumber ||
          !validateAccountNumber(trimmedAccountNumber)
        ) {
          return;
        }

        const customersResponse = await getCustomersStartsWith(
          trimmedAccountNumber,
          selectedBusinessId
        );

        if (
          !hasMatchingCustomer(
            customersResponse,
            trimmedAccountNumber,
            selectedBusinessId
          )
        ) {
          setSearchError(INVALID_ACCOUNT_NUMBER);
          return;
        }

        setSearchError("");
        await onAccountSearch(trimmedAccountNumber, selectedBusinessId);
      },
  })
)(AccountDetails);
